import React, { useMemo } from 'react';
import { getFormattedAmount } from '../../../utils';
import { calculateBucketSummary } from '../utils/bucketHelpers';

interface AgingSummaryDashboardProps {
  buckets: {
    bucketCode: string;
    bucketLabel: string;
    sortOrder: number;
  }[];
  customers: {
    aging: Record<string, number>;
    totalInvoiceAmount: number;
  }[];
}

export const AgingSummaryDashboard: React.FC<AgingSummaryDashboardProps> = ({
  buckets,
  customers,
}) => {
  // Derive overview from aging summary data only (customers + buckets); no separate outstanding API
  const summary = useMemo(() => {
    const totalAmount = customers.reduce(
      (sum, c) => sum + (c.totalInvoiceAmount ?? 0),
      0,
    );
    const notDueBucket = buckets.find(b => b.bucketCode === 'NOT_DUE');
    const notDueAmount = notDueBucket
      ? calculateBucketSummary(customers, notDueBucket.bucketCode).total
      : 0;
    const gettingDueBucket = buckets.find(b => b.bucketCode === 'GETTING_DUE');
    const gettingDueAmount = gettingDueBucket
      ? calculateBucketSummary(customers, gettingDueBucket.bucketCode).total
      : 0;
    const overdueBuckets = buckets.filter(
      b => !['NOT_DUE', 'GETTING_DUE'].includes(b.bucketCode),
    );
    const overdueAmount = overdueBuckets.reduce(
      (sum, bucket) =>
        sum + calculateBucketSummary(customers, bucket.bucketCode).total,
      0,
    );

    const notDuePercentage =
      totalAmount > 0 ? (notDueAmount / totalAmount) * 100 : 0;
    const gettingDuePercentage =
      totalAmount > 0 ? (gettingDueAmount / totalAmount) * 100 : 0;
    const overduePercentage =
      totalAmount > 0 ? (overdueAmount / totalAmount) * 100 : 0;

    return {
      totalAmount,
      totalCustomers: customers.length,
      notDue: { amount: notDueAmount, percentage: notDuePercentage },
      gettingDue: {
        amount: gettingDueAmount,
        percentage: gettingDuePercentage,
      },
      overdue: { amount: overdueAmount, percentage: overduePercentage },
    };
  }, [buckets, customers]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">
          Aging Summary Overview
        </h3>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-2">
            <span className="text-gray-600">Total Outstanding:</span>
            <span className="font-bold text-gray-900 text-base">
              {getFormattedAmount(summary.totalAmount)}
            </span>
          </div>
          <div className="h-4 w-px bg-gray-300" />
          <div className="flex items-center gap-2">
            <span className="text-gray-600">Customers:</span>
            <span className="font-semibold text-gray-900">
              {summary.totalCustomers}
            </span>
          </div>
        </div>
      </div>

      {/* Visual Bar */}
      <div className="relative w-full h-12 bg-gray-100 rounded-lg overflow-hidden mb-3 flex">
        {/* Not Due */}
        {summary.notDue.percentage > 0 && (
          <div
            className="bg-emerald-500 flex items-center justify-center transition-all duration-300 hover:opacity-90"
            style={{ width: `${summary.notDue.percentage}%` }}
            title={`Not Due: ${getFormattedAmount(summary.notDue.amount)} (${summary.notDue.percentage.toFixed(1)}%)`}
          >
            {summary.notDue.percentage >= 10 && (
              <span className="text-white text-xs font-semibold">
                {summary.notDue.percentage.toFixed(0)}%
              </span>
            )}
          </div>
        )}

        {/* Getting Due */}
        {summary.gettingDue.percentage > 0 && (
          <div
            className="bg-orange-500 flex items-center justify-center transition-all duration-300 hover:opacity-90"
            style={{ width: `${summary.gettingDue.percentage}%` }}
            title={`Getting Due: ${getFormattedAmount(summary.gettingDue.amount)} (${summary.gettingDue.percentage.toFixed(1)}%)`}
          >
            {summary.gettingDue.percentage >= 10 && (
              <span className="text-white text-xs font-semibold">
                {summary.gettingDue.percentage.toFixed(0)}%
              </span>
            )}
          </div>
        )}

        {/* Overdue */}
        {summary.overdue.percentage > 0 && (
          <div
            className="bg-red-600 flex items-center justify-center transition-all duration-300 hover:opacity-90"
            style={{ width: `${summary.overdue.percentage}%` }}
            title={`Overdue: ${getFormattedAmount(summary.overdue.amount)} (${summary.overdue.percentage.toFixed(1)}%)`}
          >
            {summary.overdue.percentage >= 10 && (
              <span className="text-white text-xs font-semibold">
                {summary.overdue.percentage.toFixed(0)}%
              </span>
            )}
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        {/* Not Due Card */}
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-3 border border-emerald-200">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
            <span className="text-xs font-semibold text-emerald-800">
              Not Due
            </span>
          </div>
          <div className="text-lg font-bold text-emerald-900">
            {getFormattedAmount(summary.notDue.amount)}
          </div>
          <div className="text-xs text-emerald-700 mt-0.5">
            {summary.notDue.percentage.toFixed(1)}% of total
          </div>
        </div>

        {/* Getting Due Card */}
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-3 border border-orange-200">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
            <span className="text-xs font-semibold text-orange-800">
              Getting Due
            </span>
          </div>
          <div className="text-lg font-bold text-orange-900">
            {getFormattedAmount(summary.gettingDue.amount)}
          </div>
          <div className="text-xs text-orange-700 mt-0.5">
            {summary.gettingDue.percentage.toFixed(1)}% of total
          </div>
        </div>

        {/* Overdue Card */}
        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-3 border border-red-200">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 bg-red-600 rounded-full"></div>
            <span className="text-xs font-semibold text-red-800">Overdue</span>
          </div>
          <div className="text-lg font-bold text-red-900">
            {getFormattedAmount(summary.overdue.amount)}
          </div>
          <div className="text-xs text-red-700 mt-0.5">
            {summary.overdue.percentage.toFixed(1)}% of total
          </div>
        </div>
      </div>
    </div>
  );
};
